import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { AccessibilityInfo, Platform, Text } from 'react-native';

// One live-region announcement for something that changes NOTHING visible
// (DS-72: the picker's result count after the return key). The three defects
// a bare `<Text accessibilityLiveRegion>` + `setState(text)` has, and what
// this does about each:
//  - The same text twice is silent (no node change): `announce` clears, then
//    sets the text on a later tick, so an identical message restarts.
//  - A message left in the tree is read STALE later (TalkBack): `clear()`,
//    called when the user goes back to editing.
//  - The region is mounted empty and stays mounted: one added AFTER the
//    change is not spoken.
// The tick lives here, with its cleanup on unmount, not in the callers.
// The node is 1x1, opacity 0: visually absent, still in the a11y tree.
// iOS live regions are inert (see ForgotPasswordForm): `speak` pushes there,
// and announces through the region elsewhere.
const TICK_MS = 50;

const HIDDEN = { position: 'absolute', width: 1, height: 1, opacity: 0 } as const;

export function useLiveAnnouncement(): {
  announce: (text: string) => void;
  speak: (text: string) => void;
  clear: () => void;
  region: ReactElement;
} {
  const [text, setText] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);
  const clear = useCallback(() => {
    cancel();
    setText('');
  }, [cancel]);
  const announce = useCallback(
    (next: string) => {
      cancel();
      setText('');
      timer.current = setTimeout(() => {
        timer.current = null;
        setText(next);
      }, TICK_MS);
    },
    [cancel],
  );
  const speak = useCallback(
    (next: string) => {
      if (Platform.OS === 'ios') {
        AccessibilityInfo.announceForAccessibility(next);
      } else {
        announce(next);
      }
    },
    [announce],
  );
  useEffect(() => cancel, [cancel]);

  const region = (
    <Text accessibilityLiveRegion="polite" style={HIDDEN}>
      {text}
    </Text>
  );
  return { announce, speak, clear, region };
}
